import { CircularProgress, Grid, LinearProgress, Link } from "@mui/material";
import DetailsPanel from "components/Elements/DetailsPanel";
import SearchField from "components/Elements/SearchField/SearchField";
import {
	isoDateWithoutTimeZone,
	debounce,
	getLocalStorageData,
	defaultPageSize,
	customFromattedDate,
} from "helpers/utils";
import React, { useCallback, useEffect, useState, useRef, useMemo } from "react";
import { useDispatch } from "react-redux";
import { showError } from "../../../redux/common/actions";
import AddNewFeedbackDetail from "./AddFeedback";
import Header from "./Header";
import { makeStyles } from "tss-react/mui";
import FeedbackListTable from "./FeedbackListTable";
import ColourConstants from "helpers/colourConstants";
import CustomDateRange from "./CustomDateRange";
import { useUserSearch } from "hooks/useUserSearch";
import DeleteDialog from "components/Elements/DeleteDialog";
import {
	getCountOfFeedbackList,
	getFeedbackList,
	postNewFeedback,
} from "services/feedback/feedbackList";
import {
	FeedbackTableColumns,
	FeedbackTableHeader,
} from "constants/FeedbackDetails";
import ErrorIcon from "@mui/icons-material/Error";
import TabTitle from "components/Elements/TabTitle";
import HtmlTooltip from "components/Elements/HtmlTooltip";
import { READONLY_ACCESS } from "constants/AccessTypes/AccessTypes";
import { appPath, feedbackPath } from "helpers/routePaths";
import { FilterProvider, FilterContainer, useFilters } from "components/Elements/Filters";

const useStyles = makeStyles()((theme) => ({
	loading: {
		position: "absolute",
		width: "100%",
		left: 0,
		top: 0,
		right: 0,
	},
}));

export const isRelevantFeedback = (feedback, me) => {
	return (
		feedback.assignUserID === me.userId ||
		(feedback.assignUserID === null &&
			feedback.assignPositionID === me.positionId) ||
		feedback.createdUserID === me.userId
	);
};

const formattedData = (data, navigate, me) => {
	return data.map((x) => ({
		...x,
		number: (
			<Link
				onClick={() => navigate(`${x.id}`)}
				href={`${appPath}${feedbackPath}/${x.id}`}
				style={{
					color: ColourConstants.activeLink,
					cursor: "pointer",
					textDecoration: "none",
				}}
			>
				{x.number}
			</Link>
		),
		safetyCritical: isRelevantFeedback(x, me) ? (
			<ErrorIcon style={{ color: ColourConstants.activeLink }} />
		) : (
			""
		),
		createdDateTime: isoDateWithoutTimeZone(x.createdDateTime + "Z"),
		benefit: (
			<HtmlTooltip title={x.benefit}>
				<p className="max-two-line">
					<Link
						onClick={() => navigate(`${x.id}`)}
						href={`${appPath}${feedbackPath}/${x.id}`}
						style={{
							color: ColourConstants.commonText,
							cursor: "pointer",
							textDecoration: "none",
						}}
					>
						{x.benefit}
					</Link>
				</p>
			</HtmlTooltip>
		),
		changeRequired: (
			<HtmlTooltip title={x.changeRequired}>
				<p className="max-two-line">
					<Link
						onClick={() => navigate(`${x.id}`)}
						href={`${appPath}${feedbackPath}/${x.id}`}
						style={{
							color: ColourConstants.commonText,
							cursor: "pointer",
							textDecoration: "none",
						}}
					>
						{x.changeRequired}
					</Link>
				</p>
			</HtmlTooltip>
		),
		feedbackStatusName: (
			<span
				style={{
					color: x?.feedbackStatusType === "C" ? ColourConstants.green : "",
				}}
			>
				{x?.feedbackStatusName}
			</span>
		),
	}));
};

const defaultCustomDate = { from: "", to: "" };

// Filter configuration for the new Filter framework
const filterConfig = {
	filters: {
		user: true,
		feedbackStatus: true,
		timeframe: {
			allowCustomRange: true,
			allowedRanges: ['today', 'yesterday', 'this-week', 'last-week', 'last-month', 'last-year', 'last-7-days', 'last-14-days', 'last-30-days', 'last-90-days', 'custom']
		},
		department: { multiSelect: true },
		tableSort: true,
	}
};

function FeedbackListsContent() {
	const { filterValues, updateFilter, allFiltersInitialized } = useFilters();
	
	// Helper to get current filter values for API calls
	const getCurrentFilterValues = () => {
		const statusValue = filterValues['feedbackStatus']?.value;
		const timeframeValue = filterValues['timeframe']?.value;
		const departmentParams = filterValues['department']?.params || {};
		const userValue = filterValues['user']?.value;
		const tableSortParams = filterValues['tableSort']?.params;

		return {
			feedbackStatusID: statusValue?.id || '',
			statusType: statusValue?.statusType || '',
			...departmentParams,
			fromDate: timeframeValue?.fromDate || '',
			toDate: timeframeValue?.toDate || '',
			myFeedback: filterValues['user']?.params?.myFeedback || false,
			userId: filterValues['user']?.params?.userId || '',
			sortField: tableSortParams?.sortField || '',
			sortOrder: tableSortParams?.sortOrder || 'asc',
		};
	};

	// init hooks
	const dispatch = useDispatch();
	const { classes, cx } = useStyles();
	const {
		allData,
		setAllData,
		searchedData,
		searchQuery,
		setSearchData,
		setSearchQuery,
	} = useUserSearch();

	// gets localstorage || sessionStorage data
	const {
		customCaptions,
		siteAppID,
		siteID,
		id,
		application,
		position: { id: positionId, feedbackAccess },
	} = getLocalStorageData("me");

	// Get current table sort from FilterFramework (convert to array format for table component)
	const tableSortParams = filterValues.tableSort?.params || { sortField: "", sortOrder: "asc" };
	const currentTableSort = [tableSortParams.sortField, tableSortParams.sortOrder];
	const [openAddFeedback, setOpenAddFeedback] = useState(false);
	const [countOFFeedback, setCountOfFeedback] = useState(0);
	const [loading, setLoading] = useState(true);
	const [isSearching, setSearching] = useState(false);
	const [deleteID, setDeleteID] = useState(null);
	const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

	const [customDate, setCustomDate] = useState(defaultCustomDate);
	const [openCustomDatePopup, setOpenCustomDatePopup] = useState(false);
	const [isCustomDateRangeError, setCustomDateRangeError] = useState(false);
	const [dataForFetchingFeedback, setDataForFetchingFeedback] = useState({
		pageNumber: 1,
		pageSize: defaultPageSize(),
		search: "",
		sortField: "",
		sort: "",
	});

	const searchRef = useRef("");

	// Effect to handle filter changes only when all filters are initialized
	useEffect(() => {
		if (!allFiltersInitialized) {
			return;
		}
		
		const handleFilterChange = async () => {
			// Reset to page 1 when filters change
			setDataForFetchingFeedback(prev => ({
				...prev,
				pageNumber: 1
			}));
			
			const params = getCurrentFilterValues();
			setSearching(true);
			
			// Clear both data and count to prevent infinite scroll from using stale values
			setAllData([]);
			setCountOfFeedback(0);
			
			await fetchFeedbackList({
				search: searchRef.current,
				...params,
				forcePageNumber: 1, // Force page 1 for filter changes
			});
			setSearching(false);
		};

		handleFilterChange();
	}, [filterValues, allFiltersInitialized]);

	// handling Custom Date input change
	const handleCustomDateChange = (type, e) => {
		setCustomDate({ ...customDate, [type]: e.target.value });
	};

	// close Custom Date popup
	const handleCloseCustomDate = () => {
		setOpenCustomDatePopup(false);
		setCustomDateRangeError(defaultCustomDate);
	};

	// when the Custom Date popup is submitted
	const handleCustomDateSubmit = async (e) => {
		e.preventDefault();

		const isFromDateEmpty = customDate.from === "";
		const isToDateEmpty = customDate.to === "";
		// show error if date fields are empty
		if (isFromDateEmpty || isToDateEmpty) {
			setCustomDateRangeError({ from: isFromDateEmpty, to: isToDateEmpty });
			return;
		}

		setSearching(true);

		const formattedCustomDate = customFromattedDate(customDate);

		// Note: The Filter framework will handle the date range state
		// This function is kept for compatibility with the CustomDateRange component
		const currentFilters = getCurrentFilterValues();
		await fetchFeedbackList({
			search: searchRef.current,
			...currentFilters,
			fromDate: formattedCustomDate.fromDate,
			toDate: formattedCustomDate.toDate,
		});
		setSearching(false);

		handleCloseCustomDate();
	};

	// attemp to fetch feedback list
	const fetchFeedbackList = useCallback(
		async ({
			search = "",
			feedbackStatusID = "",
			statusType = "",
			siteDepartmentID = "",
			siteDepartmentIds = [],
			fromDate = "",
			toDate = "",
			myFeedback = true,
			userId = "",
			sortField,
			sortOrder,
			shouldCount = true,
			forcePageNumber = null,
		}) => {
			try {
				const response = await Promise.all([
					getFeedbackList({
						feedbackStatusID: ["C", "O"].includes(feedbackStatusID)
							? ""
							: feedbackStatusID,
						statusType: ["C", "O"].includes(feedbackStatusID)
							? feedbackStatusID
							: statusType,
						siteAppId: siteAppID,
						search,
						siteDepartmentID,
						siteDepartmentIds,
						fromDate,
						toDate,
						myFeedback,
						userId,
						sortField,
						sortOrder,
						pageNumber: forcePageNumber || dataForFetchingFeedback.pageNumber,
						pageSize: dataForFetchingFeedback.pageSize,
					}),
					shouldCount &&
						getCountOfFeedbackList({
							feedbackStatusID: ["C", "O"].includes(feedbackStatusID)
								? ""
								: feedbackStatusID,
							statusType: ["C", "O"].includes(feedbackStatusID)
								? feedbackStatusID
								: statusType,
							siteAppId: siteAppID,
							search,
							siteDepartmentID,
							siteDepartmentIds,
							fromDate,
							toDate,
							myFeedback,
							userId,
						}),
				]);
				if (response[0].status) {
					setAllData(response[0].data);
					response[1].status && setCountOfFeedback(response[1].data);
					// Don't reset page here to prevent race conditions - pagination is handled by forcePageNumber parameter
				} else {
					dispatch(
						showError(
							response?.data?.detail ||
								`Failed to fetch ${customCaptions?.feedback} list`
						)
					);
				}
			} catch (error) {
				dispatch(
					showError(
						error?.response?.detail ||
							`Failed to fetch ${customCaptions?.feedback} list`
					)
				);
			}
		},
		[siteAppID, dispatch, setAllData]
	);

	// fetch initial data after mounting (excluding feedback list - handled by filters)
	useEffect(() => {
		setLoading(false);
		document.body.style.overflowX = "hidden";
		document.body.style.maxWidth = "100%";
	}, [siteID]);

	// searching for feedback
	const handleSearch = useCallback(
		debounce(
			async (value) => {
				setSearching(true);
				searchRef.current = value;
				const currentFilters = getCurrentFilterValues();
				await fetchFeedbackList({
					search: value,
					...currentFilters,
				});
				if (!value || value === "")
					setDataForFetchingFeedback({
						pageNumber: 1,
						pageSize: defaultPageSize(),
						search: "",
						sortField: "",
						sort: "",
					});
				setSearching(false);
			},
			500
		),
		[currentTableSort, fetchFeedbackList]
	);

	// calling add new feedback api
	const addNewFeedback = async (payload) => {
		return await postNewFeedback(payload);
	};

	// opens DELETE feedback  popup
	const handleDeleteDialogOpen = (id) => {
		setDeleteID(id);
		setOpenDeleteDialog(true);
	};

	// remove feedback from client table list after successfull deletion of feedback
	const handleRemoveData = (id) => {
		setAllData([...allData.filter((d) => d.id !== id)]);
		setCountOfFeedback((prev) => prev - 1);
	};

	const mainData = searchQuery.length === 0 ? allData : allData;

	if (loading) return <CircularProgress />;

	return (
		<>
			<TabTitle
				title={`${customCaptions.feedbackPlural} | ${application.name}`}
			/>
			{isSearching && <LinearProgress className={classes.loading} />}

			<DeleteDialog
				entityName={customCaptions?.feedback}
				open={openDeleteDialog}
				closeHandler={() => setOpenDeleteDialog(false)}
				deleteID={deleteID}
				deleteEndpoint={"/api/feedback"}
				handleRemoveData={handleRemoveData}
			/>
			<AddNewFeedbackDetail
				open={openAddFeedback}
				closeHandler={() => setOpenAddFeedback(false)}
				siteAppId={siteAppID}
				title={"Add " + customCaptions?.feedback}
				createProcessHandler={addNewFeedback}
				customCaptions={customCaptions}
				showAssetReferences={application?.showAssetReferences}
				setSearchQuery={setSearchQuery}
				fetchData={() => {
					const currentFilters = getCurrentFilterValues();
					return fetchFeedbackList({
						search: "",
						...currentFilters,
					});
				}}
				setDataForFetchingFeedback={setDataForFetchingFeedback}
				siteID={siteID}
			/>
			<CustomDateRange
				open={openCustomDatePopup}
				handleChange={handleCustomDateChange}
				customDate={customDate}
				closeHandler={handleCloseCustomDate}
				onSubmit={handleCustomDateSubmit}
				isError={isCustomDateRangeError}
				isLoading={isSearching}
			/>
			<div className="container">
				<Header
					setOpenAddFeedback={setOpenAddFeedback}
					dataLength={countOFFeedback}
					feedbackCC={customCaptions?.feedbackPlural}
				/>
				<div
					className="detailsContainer"
					style={{ alignItems: "center", marginTop: "-15px" }}
				>
					<DetailsPanel
						showHeader={false}
						description={`View all ${customCaptions?.feedbackPlural} across your operations`}
					/>
					<SearchField
						searchQuery={dataForFetchingFeedback?.search}
						setSearchQuery={(e) => {
							e.persist();
							setDataForFetchingFeedback((prev) => ({
								...prev,
								search: e.target.value,
							}));
							handleSearch(e.target.value);
						}}
					/>
				</div>


				{/* Filter Framework */}
				<div style={{ marginTop: 20 }}>
					<FilterContainer />
				</div>

				<div style={{ height: 20 }}></div>
				<div className="dynamic-width-table">
					<FeedbackListTable
						data={mainData}
						headers={FeedbackTableHeader(customCaptions)}
						columns={FeedbackTableColumns()}
						setData={setAllData}
						handleSort={async (sortField, sort) => {
							setSearching(true);
							
							// Create a sort option in the expected format
							const sortId = sortField ? `${sortField}_${sort}` : '';
							const sortName = sortField ? `${sortField} (${sort === 'asc' ? 'Ascending' : 'Descending'})` : 'Default Sort';
							
							// Update the tableSort filter
							updateFilter('tableSort', { id: sortId, name: sortName }, { 
								sortField, 
								sortOrder: sort 
							});
							
							// Don't fetch here - the filter change will trigger the useEffect
							setSearching(false);
						}}
						searchQuery={searchRef.current}
						formattedData={formattedData}
						searchedData={searchedData}
						setSearchData={setSearchData}
						handleDeleteDialogOpen={handleDeleteDialogOpen}
						searchText={searchRef.current}
						page={dataForFetchingFeedback.pageNumber}
						countOFFeedback={countOFFeedback}
						department={getCurrentFilterValues().siteDepartmentID}
						status={getCurrentFilterValues().feedbackStatusID}
						date={{
							fromDate: getCurrentFilterValues().fromDate,
							toDate: getCurrentFilterValues().toDate,
						}}
						setDataForFetchingFeedback={setDataForFetchingFeedback}
						siteAppID={siteAppID}
						myFeedback={getCurrentFilterValues().myFeedback}
						currentTableSort={currentTableSort}
						setCurrentTableSort={(sortArray) => {
							const sortField = sortArray[0];
							const sortOrder = sortArray[1];
							const sortId = sortField ? `${sortField}_${sortOrder}` : '';
							const sortName = sortField ? `${sortField} (${sortOrder === 'asc' ? 'Ascending' : 'Descending'})` : 'Default Sort';
							
							updateFilter('tableSort', 
								{ id: sortId, name: sortName }, 
								{ sortField, sortOrder }
							);
						}}
						userId={id}
						positionId={positionId}
						content={customCaptions?.feedback}
						isReadOnly={feedbackAccess === READONLY_ACCESS}
					/>
				</div>
			</div>
		</>
	);
}

// Wrapper component that provides FilterContext
function FeedbackLists() {
	return (
		<FilterProvider 
			config={filterConfig}
			storageKey="feedback"
		>
			<FeedbackListsContent />
		</FilterProvider>
	);
}

export default FeedbackLists;
